// Features that are switched on per user, on top of the role check in routes/AdminRoutes.jsx.
// A route (or anything else) names a feature; the switch for it is a school setting stored in the backend, one per
// user and named FEATURE_X:<username> (see actions/admin/featureFlags.js), so it can be changed without
// deploying: the user has the feature when that setting is ENABLE (or ENABLED). A feature that isn't listed is
// open to everyone. The Feature Access page (views/admin/FeatureAccess) is where these settings are changed.
//
// This only hides the UI. The backend doesn't check these settings, so it is not a security boundary.
//
// Nothing is behind a switch right now (Transport was, until it was rolled out to every admin). To gate the next
// feature, add `feature: 'name'` to its route in routes/AdminRoutes.jsx and an entry here, for example:
//   { key: 'name', configName: 'FEATURE_NAME', label: 'Name', description: '...',
//     roleLabel: 'Admin role only', allowEmployeeLogins: false }
export const FEATURES = [];

export const FEATURE_CONFIG_NAMES = Object.fromEntries(FEATURES.map(feature => [feature.key, feature.configName]));

export const ON_VALUES = ['ENABLE', 'ENABLED'];

export const isOn = value => ON_VALUES.includes(String(value || '').trim().toUpperCase());

export const isFeatureEnabled = (feature, featureFlags) => {
  if (!feature) return true;
  return isOn(featureFlags?.[FEATURE_CONFIG_NAMES[feature]]);
};

// A setting for EVERYONE turns the feature on for every login that has the right role (the route's accessRoles);
// a setting for a specific username overrides it, in either direction.
export const EVERYONE = '*';

// Usernames are emails and unique. Like the login page, a bare "adminobs" means adminobs@gmail.com.
export const normalizeUsername = username => {
  const trimmed = (username || '').trim().toLowerCase();
  if (!trimmed || trimmed === EVERYONE) return trimmed;
  return trimmed.includes('@') ? trimmed : `${trimmed}@gmail.com`;
};

// The school setting that switches one feature on or off for one username, e.g. FEATURE_TRANSPORT:adminobs@gmail.com.
export const featureSettingName = (feature, username) => `${feature.configName}:${normalizeUsername(username)}`;

// Who may open the Feature Access page: the logins named (comma separated) in REACT_APP_CONFIG_ADMIN_EMAILS,
// which is read when the app is built. Unset means nobody.
const configAdminEmails = (process.env.REACT_APP_CONFIG_ADMIN_EMAILS || '')
  .split(',')
  .map(email => email.trim().toLowerCase())
  .filter(Boolean);

export const isConfigAdmin = email => configAdminEmails.includes((email || '').trim().toLowerCase());
